import { router } from "@/router";
import { useAuthStore } from "@/stores/auth";
import { useBrandSwitcherStore } from "@/stores/brandSwitcher";

/**
 * `fetch` wrapper for endpoints under /api/v1/* that need a JWT
 * bearer header. Centralises two things the typed `openapi-fetch`
 * client gives us automatically but plain `fetch()` callers don't:
 *
 *  - Reads the access token from the Pinia auth store and injects
 *    it as the Authorization header.
 *  - On a 401 response (expired or revoked session), clears the
 *    auth state and bounces to /login carrying the current path as
 *    `redirect`, so after re-login the user lands back where they
 *    were — instead of staring at a blank page full of "Failed to
 *    load X" errors.
 *
 * Stores that use action POSTs / PATCHes (DTOs without @ApiBody)
 * should call this instead of `fetch` so 401 handling stays
 * consistent across the GUI.
 */
export async function authFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const auth = useAuthStore();
  const switcher = useBrandSwitcherStore();
  const headers = new Headers(init.headers ?? {});
  if (auth.tokens?.accessToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${auth.tokens.accessToken}`);
  }
  // Brand-switcher: when an internal user has picked a brand, every
  // admin API request carries `X-Brand-Code` so the BrandContext
  // middleware on /api/v1/* can scope queries to that brand. No
  // header = admin / cross-brand (the default).
  if (switcher.selectedCode && !headers.has("X-Brand-Code")) {
    headers.set("X-Brand-Code", switcher.selectedCode);
  }
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    handleUnauthenticated();
  }
  return res;
}

/**
 * Shared 401 handler — logout + redirect to /login. Exposed so the
 * typed `openapi-fetch` middleware can call the same thing.
 */
export function handleUnauthenticated(): void {
  const auth = useAuthStore();
  const wasAuthenticated = auth.isAuthenticated;
  if (wasAuthenticated) {
    auth.logout();
    auth.error = "Kirjautumissessiosi vanheni. Kirjaudu uudelleen.";
  }
  const current = router.currentRoute.value;
  if (current.name === "login") return;
  void router.push({
    path: "/login",
    query: { redirect: current.fullPath },
  });
}
